import * as React from "react";
import { IDataSet, TooltipInfo } from "../Data";
import { Tooltip } from "./Tooltip";
import { TimeSeries } from "./TimeSeries";
import { Key } from "../../models/index";
import { TimeSeriesActions } from "./flux/TimeSeriesActions";
import { Polyglot } from "../../polyglot";

const styles = require('./TimeSeries.local.css');

interface Props
{
    polyglot: Polyglot;
    dataSets: IDataSet[];
    zoomedDataSets: IDataSet[];
    tooltip: TooltipInfo;
    isRangeOk?: (range: [Date, Date]) => boolean;
}

interface State
{
    width: number;
    height: number;

    clientX: number;
    clientY: number;
    lock: boolean;
}

// This components is a container for TimeSeries and Tooltip widgets
export class Chart extends React.Component<Props, State>
{
    private readonly timeSeriesActions = new TimeSeriesActions();
    private tooltip: HTMLDivElement;
    
    constructor(props: Props)
    {
        super(props);
        this.state = {
            // canvas size for Timeseries component
            width: undefined,
            height: undefined,

            // location of the tooltip
            clientX: undefined,
            clientY: undefined,
            
            lock: false, // freeze tooltip when user holds Alt key
        };
    }
    
    render()
    {
        const { polyglot, dataSets, zoomedDataSets, tooltip } = this.props;
        const { width, height, lock, clientX, clientY } = this.state;

        return (
            <div style={{height: 500, minWidth: 300}} ref={this.updateSize}
                onMouseMove={this.onMouseMove}>
                <TimeSeries className={lock ? styles.NoEvents : null}
                    dataSets={dataSets} zoomedDataSets={zoomedDataSets} tooltip={tooltip}
                    width={width} height={height}
                    onSweepLine={this.handleSweepLine}
                    isRangeOk={this.props.isRangeOk}
                />
                <Tooltip
                    polyglot={polyglot}
                    className={lock ? styles.LockedTooltip : null} info={tooltip} left={clientX} top={clientY}
                    elemRef={e => this.tooltip = e}
                    onToggle={(seqId: Key) => this.timeSeriesActions.toggleVisibility(seqId)}
                    highlight={(seqId: Key) => this.timeSeriesActions.highlightSingle(seqId)}
                    normolize={() => this.timeSeriesActions.normalize()}
                />
            </div>
        );
    }

    private onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const { lock } = this.state;
        const nextLock = e.altKey ? true : false;
        if(lock !== nextLock) {
            this.setState({ lock: nextLock });
        }
    }

    private handleSweepLine = (e: { clientX: number, clientY: number }) => {
        const { lock, clientX, clientY } = this.state;
        this.setState({
            clientX: lock ? clientX : this.calcTooltipX(e.clientX),
            clientY: lock ? clientY : this.calcTooltipY(e.clientY),
        });
    }

    private calcTooltipX = (sweepLineX: number) => {
        if(this.tooltip) {
            const gap = 10;
            const E = document.body.clientWidth;
            const { clientWidth: width } = this.tooltip;
            return (sweepLineX + gap + width < E)
                ? sweepLineX + gap // put tooltip to the right side of sweepline
                : sweepLineX - width - gap // put tooltip to the left side of sweepline
        }
        return sweepLineX;
    }

    private calcTooltipY = (sweepLineY: number) => {
        if(this.tooltip) {
            const S = document.body.clientHeight;
            const { clientHeight: height } = this.tooltip;
            const dy = S - (sweepLineY + height);
            return dy > 0 ? sweepLineY : sweepLineY + dy;
        }
    }

    // updateSize is called when React has mounted/unmounted the div container
    // at this point we can query div's size in order to inject these values to TimeSeries
    private updateSize = (div: HTMLDivElement) => {
        if(div) {
            const { clientWidth, clientHeight } = div;
            this.setState({ width: clientWidth, height: clientHeight });
        }
    }
}


// WEBPACK FOOTER //
// ./src/charts/TimeSeries/Chart.tsx